import React, {useMemo} from "react";
import {Col, Row} from "reactstrap";
import {InputForm} from "../../base/input/input";
import Button from "../../base/button/button";
import Form from "../../base/form";
import {DatePickerForm} from "../../base/date-picker";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import DateUtils from "../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../form';

const schema = Yup.object().shape({
    keyword: Yup.string().nullable(),
    startDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref('endDate'), ValidateMessages.lessThan('End Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
    endDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref('startDate'), ValidateMessages.greaterThan('Start Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
    minAmount: Yup.number()
        .nullable()
        .typeError(ValidateMessages.incorrectType('Number'))
        .positive(ValidateMessages.greaterThan('0'))
        .maxNumber(Yup.ref('maxAmount'), ValidateMessages.lessThan('Max amount')),
    maxAmount: Yup.number()
        .nullable()
        .typeError(ValidateMessages.incorrectType('Number'))
        .positive(ValidateMessages.greaterThan('0'))
        .minNumber(Yup.ref('minAmount'), ValidateMessages.greaterThan('Min amount')),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const AvailableCreditsSearchSection = ({filters, setFilters}) => {
    const initialValues = useMemo(() => ({
        ...filters,
        minAmount: filters?.minAmount?.toString() ?? undefined,
        maxAmount: filters?.maxAmount?.toString() ?? undefined,
        startDate: filters?.startDate ? DateUtils.serverToUTC(filters?.startDate) : undefined,
        endDate: filters?.endDate ? DateUtils.serverToUTC(filters?.endDate) : undefined,
    }), [filters])

    /**
     * Submits the search results by invoking the given [setFilters] callback.
     * @param values {any} form values
     */
    const onSubmit = (values) => {
        setFilters({
            ...values,
            startDate: values.startDate ? DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values.endDate ? DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat) : undefined,
        });
    }

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({submitting, values}) => {
                const colClassName = 'px-2 mb-4';
                return (
                    <Row className='no-gutters align-items-center justify-content-center mt-4'>
                        <Col xs={12} md={6} className={colClassName}>
                            <InputForm
                                label={'Keywords'}
                                name={'keyword'}
                                type={'text'}
                                required={required.keyword}
                            />
                        </Col>
                        <Col xs={6} md={3} className={colClassName}>
                            <InputForm
                                label={'Min Amount'}
                                name={'minAmount'}
                                type={'number'}
                                required={required.minAmount}
                            />
                        </Col>
                        <Col xs={6} md={3} className={colClassName}>
                            <InputForm
                                label={'Max Amount'}
                                name={'maxAmount'}
                                type={'number'}
                                required={required.maxAmount}
                            />
                        </Col>
                        <Col xs={6} className={colClassName}>
                            <DatePickerForm
                                name={'startDate'}
                                label={'Start Date'}
                                required={required.startDate}
                                maxDate={values.endDate}
                                disableFuture
                            />
                        </Col>
                        <Col xs={6} className={colClassName}>
                            <DatePickerForm
                                name={'endDate'}
                                label={'End Date'}
                                required={required.endDate}
                                minDate={values.startDate}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12}
                             className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                            <Button
                                disabled={!!submitting}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='py-3 px-8'
                            >
                                Search
                            </Button>
                        </Col>
                    </Row>
                );
            }}
        />
    );
}

export default AvailableCreditsSearchSection;
